/**
 * Mix & Match builder: pick three eligible items, the cheapest comes off at checkout.
 *
 * The section renders every eligible product server-side, so this only handles state: filling slots,
 * working out the saving, and adding all three to the cart in one request. Without JS the section still
 * shows the offer and links to the collection, so nothing is lost.
 *
 * The free item is applied by the automatic Buy X Get Y discount in the admin, NOT here — this only
 * previews what that discount will do. If the discount is ever removed, the preview lies, so the two
 * belong together.
 */
class PgkMixMatch extends HTMLElement {
  connectedCallback() {
    this.slots = [...this.querySelectorAll("[data-slot]")];
    this.rail = this.querySelector("[data-rail]");
    this.status = this.querySelector("[data-status]");
    this.submit = this.querySelector("[data-submit]");
    this.summary = this.querySelector("[data-summary]");
    this.picked = [];
    this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";

    this.rail.addEventListener("click", (e) => {
      const card = e.target.closest("[data-variant-id]");
      if (card) this.pick(card);
    });
    this.slots.forEach((slot, i) => slot.addEventListener("click", () => this.unpick(i)));
    this.submit.addEventListener("click", () => this.addToCart());
    this.render();
  }

  money(cents) {
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  pick(card) {
    if (this.picked.length >= 3) {
      this.announce("You already have three. Remove one to swap it.");
      return;
    }
    this.picked.push({
      id: card.dataset.variantId,
      title: card.dataset.title,
      price: Number(card.dataset.price),
      image: card.dataset.image,
      handle: card.dataset.handle,
    });
    this.render();
  }

  unpick(index) {
    if (!this.picked[index]) return;
    this.picked.splice(index, 1);
    this.render();
  }

  render() {
    this.slots.forEach((slot, i) => {
      const item = this.picked[i];
      slot.classList.toggle("is-filled", Boolean(item));
      slot.innerHTML = item
        ? `<span class="pgk-slot__tag">Item ${i + 1}</span>
           <img src="${item.image}" alt="" loading="lazy" width="200" height="200">
           <span class="pgk-slot__name">${item.title}</span>
           <span class="pgk-slot__remove" aria-hidden="true">Remove</span>`
        : `<span class="pgk-slot__tag">Item ${i + 1}</span><span class="pgk-slot__plus" aria-hidden="true">+</span>`;
      slot.setAttribute(
        "aria-label",
        item ? `Item ${i + 1}: ${item.title}. Press to remove.` : `Item ${i + 1}: empty`,
      );
      slot.disabled = !item;
    });

    // Mark what's already chosen so the same product can't be added twice by accident.
    this.rail.querySelectorAll("[data-variant-id]").forEach((card) => {
      const chosen = this.picked.some((p) => p.id === card.dataset.variantId);
      card.classList.toggle("is-chosen", chosen);
      card.setAttribute("aria-pressed", String(chosen));
    });

    const total = this.picked.reduce((sum, p) => sum + p.price, 0);
    const free = this.picked.length === 3 ? Math.min(...this.picked.map((p) => p.price)) : 0;
    this.submit.disabled = this.picked.length !== 3;
    this.submit.querySelector("[data-submit-label]").textContent =
      this.picked.length === 3 ? `Add 3 to bag — ${this.money(total - free)}` : `Pick ${3 - this.picked.length} more`;
    this.summary.textContent = free
      ? `${this.money(total)} − ${this.money(free)} free = ${this.money(total - free)}`
      : "";
    if (this.picked.length && this.picked.length < 3) {
      this.announce(`${this.picked.length} of 3 chosen.`);
    } else if (this.picked.length === 3) {
      const cheapest = this.picked.find((p) => p.price === free);
      this.announce(`Three chosen. ${cheapest.title} comes off at checkout, saving ${this.money(free)}.`);
    }
  }

  announce(message) {
    this.status.textContent = message;
  }

  async addToCart() {
    this.submit.disabled = true;
    const label = this.submit.querySelector("[data-submit-label]");
    const previous = label.textContent;
    label.textContent = "Adding…";
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: this.picked.map((p) => ({ id: Number(p.id), quantity: 1 })) }),
      });
      if (!res.ok) throw new Error((await res.json()).description || "Could not add to bag");
      window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
    } catch (e) {
      label.textContent = previous;
      this.submit.disabled = false;
      this.announce(`${e.message}. Please try again.`);
    }
  }
}
customElements.define("pgk-mix-match", PgkMixMatch);
